import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Coins, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import {
	type CoinPack,
	CoinPackService,
	TopUpNotEligibleError,
} from "@/lib/coin/coinPack.service";
import { useCoinBalance, useCoinStore } from "@/lib/coin/coin.store";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import {
	preOpenCheckoutWindow,
	watchCheckoutWindowClose,
} from "@/lib/stripe/checkoutWindow";

/**
 * Coins are credited by the Stripe webhook, which can land a moment after the
 * browser returns from checkout. Re-read the balance until the credit shows up
 * so the user (and anything resuming a blocked action) never sees a stale total.
 */
const BALANCE_POLL_DELAYS_MS = [0, 1000, 2000, 2500];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface CoinTopUpProps {
	/** Analytics source, e.g. "pricing_page" or "workspace_modal". */
	source?: string;
	/**
	 * Called once the purchased coins are on the balance. Lets a host that
	 * opened this to unblock something — the create-podcast flow, say — get out
	 * of the way instead of leaving the user parked on the payment screen.
	 */
	onPurchaseComplete?: () => void;
}

/**
 * One-time coin top-ups for users who already pay for a plan.
 *
 * Renders nothing unless the backend reports the user as eligible, so
 * non-subscribers only ever see the plan picker.
 */
const CoinTopUp = ({
	source = "pricing_page",
	onPurchaseComplete,
}: CoinTopUpProps) => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { balance } = useCoinBalance();
	const [purchasing, setPurchasing] = useState<string | null>(null);
	const stopCheckoutWatchRef = useRef<(() => void) | null>(null);
	useEffect(() => () => stopCheckoutWatchRef.current?.(), []);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["coin-packs"],
		queryFn: () => CoinPackService.getPacks(),
		staleTime: 5 * 60 * 1000,
	});

	useEffect(() => {
		if (isError) {
			toast.error(t("pricing.topUp.toasts.loadFailed"));
		}
	}, [isError, t]);

	/**
	 * Give up after the last delay rather than blocking forever: the payment
	 * did succeed, so it is better to continue on a stale balance than to trap
	 * the user while a webhook is backed up.
	 */
	const awaitCreditedCoins = useCallback(async () => {
		const balanceBefore = useCoinStore.getState().balance;

		for (const delay of BALANCE_POLL_DELAYS_MS) {
			if (delay > 0) await sleep(delay);
			await useCoinStore.getState().refreshBalance();

			const current = useCoinStore.getState().balance;
			if (
				balanceBefore === null ||
				(current !== null && current > balanceBefore)
			) {
				return;
			}
		}
	}, []);

	// Held in a ref so callers can pass an inline callback without churning the
	// identity of the success handler the effects below depend on.
	const onPurchaseCompleteRef = useRef(onPurchaseComplete);
	useEffect(() => {
		onPurchaseCompleteRef.current = onPurchaseComplete;
	}, [onPurchaseComplete]);

	const handleTopUpSuccess = useCallback(async () => {
		setPurchasing(null);
		toast.success(t("pricing.topUp.toasts.success"));
		await awaitCreditedCoins();
		onPurchaseCompleteRef.current?.();
	}, [awaitCreditedCoins, t]);

	// Stripe returns to /pricing?topup=... — in the checkout popup, hand the
	// result to the opener and close; in the main window, handle it directly.
	useEffect(() => {
		const topup = searchParams.get("topup");
		if (!topup) return;

		if (window.opener && !window.opener.closed) {
			window.opener.postMessage(
				{
					type:
						topup === "success" ? "COIN_TOPUP_SUCCESS" : "COIN_TOPUP_CANCELLED",
				},
				window.location.origin,
			);
			window.close();
			return;
		}

		if (topup === "success") {
			void handleTopUpSuccess();
		} else if (topup === "cancelled") {
			toast.error(t("pricing.topUp.toasts.cancelled"));
		}
		setSearchParams({});
	}, [searchParams, setSearchParams, handleTopUpSuccess, t]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;

			if (event.data.type === "COIN_TOPUP_SUCCESS") {
				void handleTopUpSuccess();
			} else if (event.data.type === "COIN_TOPUP_CANCELLED") {
				setPurchasing(null);
				toast.error(t("pricing.topUp.toasts.cancelled"));
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [handleTopUpSuccess, t]);

	const handleBuy = async (pack: CoinPack) => {
		const checkoutWindow = preOpenCheckoutWindow("stripe-coin-pack-checkout");

		try {
			setPurchasing(pack.priceId);
			AnalyticsService.track("coin_topup_initiated", {
				price_id: pack.priceId,
				coins: pack.coins,
				price: pack.price,
				currency: pack.currency.toUpperCase(),
				source,
			});
			const openedWindow = await CoinPackService.purchasePack(
				pack.priceId,
				checkoutWindow,
			);

			stopCheckoutWatchRef.current?.();
			stopCheckoutWatchRef.current = watchCheckoutWindowClose(
				openedWindow,
				() => setPurchasing(null),
			);
		} catch (error) {
			console.error("Failed to start coin pack checkout:", error);
			toast.error(
				error instanceof TopUpNotEligibleError
					? t("pricing.topUp.toasts.notEligible")
					: t("pricing.topUp.toasts.failed"),
			);
			setPurchasing(null);
		}
	};

	if (isLoading || !data?.eligible || data.packs.length === 0) {
		return null;
	}

	return (
		<section className="rounded-2xl border border-border bg-card p-6 md:p-7">
			<div className="mb-6">
				<h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
					<Coins className="h-5 w-5 text-orange-500" />
					{t("pricing.topUp.headline")}
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					{t("pricing.topUp.subheadline")}
				</p>
				{balance !== null && (
					<p className="mt-2 text-sm font-medium text-foreground">
						{t("pricing.topUp.currentBalance", {
							balance: balance.toLocaleString(),
						})}
					</p>
				)}
			</div>

			<div className="grid gap-4 sm:grid-cols-2">
				{data.packs.map((pack) => {
					const isPurchasing = purchasing === pack.priceId;
					const pricePerHundred = ((pack.price / pack.coins) * 100).toFixed(2);

					return (
						<div
							key={pack.priceId}
							className={[
								"relative flex flex-col rounded-xl border p-5 transition-all hover:shadow-md",
								pack.highlight
									? "border-primary bg-primary/5"
									: "border-border bg-background",
							].join(" ")}
						>
							{pack.highlight && (
								<span className="absolute -top-2.5 left-4 inline-flex items-center rounded-full border border-primary bg-primary px-2.5 py-0.5 text-[11px] font-medium text-primary-foreground">
									{t("pricing.topUp.bestValue")}
								</span>
							)}

							<div className="mb-1 flex items-baseline gap-2">
								<span className="text-2xl font-semibold text-foreground">
									{t("pricing.topUp.coinsAmount", {
										coins: pack.coins.toLocaleString(),
									})}
								</span>
							</div>

							<div className="mb-3 flex items-baseline gap-2">
								<span className="text-xl font-semibold text-foreground">
									€{pack.price.toFixed(2)}
								</span>
								<span className="text-xs text-muted-foreground">
									{t("pricing.topUp.perHundred", { price: pricePerHundred })}
								</span>
							</div>

							<ul className="mb-5 flex-1 space-y-1.5 text-xs text-muted-foreground">
								<li className="flex items-center gap-1.5">
									<Zap className="h-3.5 w-3.5 text-primary" />
									{t("pricing.topUp.instantDelivery")}
								</li>
								<li className="flex items-center gap-1.5">
									<Zap className="h-3.5 w-3.5 text-primary" />
									{t("pricing.topUp.neverExpires")}
								</li>
							</ul>

							<button
								type="button"
								onClick={() => handleBuy(pack)}
								disabled={isPurchasing}
								aria-busy={isPurchasing}
								className={[
									"w-full rounded-full px-4 py-2.5 text-sm font-medium transition-all",
									"disabled:cursor-not-allowed disabled:opacity-50",
									pack.highlight
										? "bg-primary text-primary-foreground hover:bg-primary/90"
										: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
								].join(" ")}
							>
								{isPurchasing ? (
									<>
										<Loader2 className="mr-2 inline-block h-4 w-4 animate-spin" />
										{t("common.processing")}
									</>
								) : (
									t("pricing.topUp.buy")
								)}
							</button>
						</div>
					);
				})}
			</div>
		</section>
	);
};

export default CoinTopUp;
